import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import SelectInputTranslated from 'components/layout/forms/SelectInputTranslated';
import translations from 'services/translations/translations';

import actionTypes from '../types/actionTypes';
import EditWall from './src/editWall/EditWall';
import MoveBuildingWallWithThickness from './src/editWall/MoveBuildingWallWithThickness';
import RemoveWall from './src/editWall/RemoveWall';
import SwitchInsideOutside from './src/editWall/SwitchInsideOutside';
import { BUILDING_WALL } from '../types/wallTypes';
import { TWO_D } from '../types/dimensions';

import 'style/configurator/RightBar.css';
import AddConnectionByWallLine from './src/AddConnectionByWallLine';
import { allowedToWriteOwnOrganizationConfiguration } from '../services/permissions/allowedToWriteOwnOrganizationConfiguration';
import { configurationChanged } from '../../../store/configurationChange/actions';

const RightBar = ({
  dimensionStatus,
}: {
  dimensionStatus: string;
}): JSX.Element | null => {
  const selectedWall = useSelector((state: any) => state.selectedWall.raw);
  const { t } = useTranslation([translations.common]);
  const wall: any = useSelector((state: any) => state.selectedWall.properties);
  const wallMetaData = useSelector((state: any) => state.selectedWall.metaData);
  const currentUser = useSelector((state: any) => state.users.current);
  const configuration = useSelector((state: any) => state.configuration);

  const [actionType, setActionType] = useState(actionTypes.editWall);
  const dispatch = useDispatch();

  useEffect(() => {
    setActionType(actionTypes.editWall);
  }, [selectedWall]);

  const showBar =
    selectedWall &&
    wall !== null &&
    wallMetaData !== null &&
    dimensionStatus === TWO_D;

  if (!showBar) {
    return null;
  }

  const getAvailableActions = () => {
    const defaultAvailableActions = [
      actionTypes.addConnectionByWallLine,
      actionTypes.editWall,
      actionTypes.removeWall,
      actionTypes.switchInsideOutside,
    ];

    if (selectedWall.properties.wallType === BUILDING_WALL) {
      return [
        ...defaultAvailableActions,
        actionTypes.moveBuildingWallWithThickness,
      ];
    }

    return defaultAvailableActions;
  };

  const triggerEscape = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
  };

  const closeRightBar = () => {
    triggerEscape();
    dispatch(configurationChanged());
  };

  const hasWritePermissions =
    currentUser.allowedToWriteConfigurations ||
    allowedToWriteOwnOrganizationConfiguration(
      currentUser,
      configuration?.project
    );

  const getActionType = () => {
    if (actionType === actionTypes.addConnectionByWallLine) {
      return (
        <AddConnectionByWallLine actionTriggeredCallback={closeRightBar} />
      );
    }

    if (actionType === actionTypes.editWall) {
      return (
        <EditWall
          closeRightBar={closeRightBar}
          disabledInputs={!hasWritePermissions}
        />
      );
    }

    if (actionType === actionTypes.removeWall) {
      return <RemoveWall actionTriggeredCallback={closeRightBar} />;
    }

    if (actionType === actionTypes.moveBuildingWallWithThickness) {
      return (
        <MoveBuildingWallWithThickness
          actionTriggeredCallback={closeRightBar}
        />
      );
    }

    if (actionType === actionTypes.switchInsideOutside) {
      return <SwitchInsideOutside actionTriggeredCallback={closeRightBar} />;
    }

    return null;
  };

  const onActionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActionType(e.target.value);
  };

  return (
    <div className="configurator-right-bar">
      <h2 id="close-actions" onClick={closeRightBar}>
        ×
      </h2>
      {hasWritePermissions ? (
        <SelectInputTranslated
          label={t(`${translations.common}:Action`)}
          onChange={onActionChange}
          optionKeyBase="edit-wall-actions"
          options={getAvailableActions()}
          translationPath={`${translations.common}:availableActions`}
          value={actionType}
        />
      ) : null}
      {hasWritePermissions ? <hr /> : null}
      {getActionType()}
    </div>
  );
};

export default RightBar;
